"use client";

import { cva } from "class-variance-authority";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Scrollbar } from "@/components/ui/Scrollbar";

export type FeaturedCollectionCarouselLayout = "thirds" | "pair";

interface FeaturedCollectionCarouselProps {
	items: React.ReactNode[];
	/**
	 * When true, desktop also runs the carousel and slides snap to the chosen
	 * `desktopLayout`. When false, desktop uses an even flex split and the
	 * carousel only activates on mobile.
	 */
	alwaysActive?: boolean;
	/** Slides visible per snap on desktop when the carousel is active. */
	desktopLayout?: FeaturedCollectionCarouselLayout;
	bleedMobile?: boolean;
	className?: string;
	darkMode?: boolean;
}

const trackStyles = cva(
	"flex flex-nowrap items-start gap-grid-grid-gutter-md [&>*]:basis-2/3 [&>*]:shrink-0 [&>*]:grow-0 [&>*:last-child]:mr-4 sm:[&>*:last-child]:mr-0",
	{
		variants: {
			desktop: {
				thirds: "sm:[&>*]:basis-1/3 sm:[&>*]:max-w-full",
				pair: "sm:[&>*]:basis-1/3 lg:[&>*]:basis-1/2 sm:[&>*]:max-w-full lg:[&>*]:max-w-full",
				fill: "sm:[&>*]:basis-0 sm:[&>*]:max-w-full sm:[&>*]:flex-1",
			},
			bleed: { true: "px-4 md:px-0", false: "" },
		},
		defaultVariants: { desktop: "thirds", bleed: false },
	},
);

/**
 * Horizontally-scrollable product carousel used by FeaturedCollection
 * variants. Mobile is always carousel; desktop is opt-in via `alwaysActive`.
 */
export const FeaturedCollectionCarousel = ({
	items,
	alwaysActive,
	desktopLayout = "thirds",
	bleedMobile = false,
	className,
	darkMode,
}: FeaturedCollectionCarouselProps) => {
	const needsCarousel = items.length > 1;
	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: needsCarousel && (alwaysActive ?? true),
		align: "start",
		containScroll: "trimSnaps",
		slidesToScroll: "auto",
		breakpoints: needsCarousel
			? { "(max-width: 639px)": { active: true, align: "center" as const } }
			: undefined,
	});
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [snapCount, setSnapCount] = useState(0);

	useEffect(() => {
		if (!emblaApi) return;
		const sync = () => {
			setSelectedIndex(emblaApi.selectedScrollSnap());
			setSnapCount(emblaApi.scrollSnapList().length);
		};
		emblaApi.on("select", sync);
		emblaApi.on("reInit", sync);
		sync();
		return () => {
			emblaApi.off("select", sync);
			emblaApi.off("reInit", sync);
		};
	}, [emblaApi]);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	const showScrollbar = snapCount > 1;

	return (
		<div className={twMerge("flex flex-col gap-grid-component-spacing-md", className)}>
			<div
				ref={emblaRef}
				className={twMerge(
					"w-full overflow-hidden",
					bleedMobile && "w-[calc(100%+2rem)] md:w-full -mx-4 md:mx-0",
					!alwaysActive && "sm:overflow-visible",
				)}
			>
				<div
					className={trackStyles({
						desktop: alwaysActive ? desktopLayout : "fill",
						bleed: bleedMobile,
					})}
				>
					{items}
				</div>
			</div>
			{showScrollbar && (
				<div className={alwaysActive ? "" : "sm:hidden"}>
					<Scrollbar
						current={selectedIndex + 1}
						total={snapCount}
						onPrev={scrollPrev}
						onNext={scrollNext}
						darkMode={darkMode}
					/>
				</div>
			)}
		</div>
	);
};
